import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import Pagination from "../../../../components/Pagination/Pagination";
import RequirementsTable from "./RequirementsTable";
import ScreeningQuestionsTable from "./ScreeningQuestionsTable";
import { SparklingIcon, SparklingIcon2 } from "./Svgs";
import SourceViewModal from "./SourceViewModal";
import AddRequirementModal from "./AddRequirementModal";
import { ExtractionProgressList, ExtractionProgressPanel, summarize } from "./ExtractionProgress";
import { toast } from "react-hot-toast";
import Cookies from "js-cookie";
import api from "../../../chat/api";

const getVerdictConfig = (score, verdict) => {
  const labels = {
    strong_position: "Strong Position",
    needs_improvement: "Needs Improvement",
    not_recommended: "Not Recommended",
    "no-bid": "No Bid",
    go: "Go",
  };

  let defaultLabel = verdict
    ? verdict
        .split(/[-_]/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "Analysis";
  const label = labels[verdict] || defaultLabel;

  if (score >= 80) {
    return { label, color: "#258F40", bgColor: "#EBF7ED", borderColor: "#258F40B2" };
  } else if (score >= 50) {
    return { label, color: "#D97706", bgColor: "#FFFBEB", borderColor: "#D97706B2" };
  } else {
    return { label, color: "#DC2626", bgColor: "#FEF2F2", borderColor: "#DC2626B2" };
  }
};

// --- Inline icons for the initial "Requirement Analysis" screen ---
const ReqCheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="12" fill="#8B55DD" fillOpacity="0.22" />
    <mask id="req_mask" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
      <rect width="24" height="24" fill="#D9D9D9" />
    </mask>
    <g mask="url(#req_mask)">
      <path
        d="M9.9998 13.6L15.8998 7.69999C16.0831 7.51665 16.3165 7.42499 16.5998 7.42499C16.8831 7.42499 17.1165 7.51665 17.2998 7.69999C17.4831 7.88332 17.5748 8.11665 17.5748 8.39999C17.5748 8.68332 17.4831 8.91665 17.2998 9.09999L10.6998 15.7C10.4998 15.9 10.2665 16 9.9998 16C9.73314 16 9.4998 15.9 9.2998 15.7L6.6998 13.1C6.51647 12.9167 6.4248 12.6833 6.4248 12.4C6.4248 12.1167 6.51647 11.8833 6.6998 11.7C6.88314 11.5167 7.11647 11.425 7.3998 11.425C7.68314 11.425 7.91647 11.5167 8.0998 11.7L9.9998 13.6Z"
        fill="#8B55DD"
      />
    </g>
  </svg>
);

const ReqSparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
    <path
      d="M16.625 0.875L15.5225 3.28125L13.125 4.375L15.5225 5.4775L16.625 7.875L17.7188 5.4775L20.125 4.375L17.7188 3.28125M7.875 3.5L5.6875 8.3125L0.875 10.5L5.6875 12.6875L7.875 17.5L10.0625 12.6875L14.875 10.5L10.0625 8.3125M16.625 13.125L15.5225 15.5225L13.125 16.625L15.5225 17.7188L16.625 20.125L17.7188 17.7188L20.125 16.625L17.7188 15.5225"
      fill="url(#req_sparkle)"
    />
    <defs>
      <linearGradient id="req_sparkle" x1="10.5" y1="0.875" x2="10.5" y2="20.125" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="#CCD1FF" />
      </linearGradient>
    </defs>
  </svg>
);

const REQ_CHECKLIST = [
  "Project Requirments",
  "RFP Source",
  "Aligned, Not-Aligned Status",
  "Matched Evidence",
];

// Faint blurred placeholder of a requirements table behind the prompt card.
const ReqBackgroundPreview = () => (
  <div
    aria-hidden="true"
    className="absolute inset-0 px-8 py-6 overflow-hidden select-none pointer-events-none blur-[2px] opacity-60"
  >
    <div className="flex gap-8 mb-4">
      <div className="h-4 bg-gray-200 rounded w-[140px]" />
      <div className="h-4 bg-gray-200 rounded w-[120px] ml-auto" />
    </div>
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="flex items-center gap-6 py-3 border-b border-gray-100">
        <div className="h-3 bg-gray-200 rounded flex-1" />
        <div className="h-3 bg-gray-200 rounded w-[90px]" />
        <div className="h-5 bg-purple-100 rounded w-[80px]" />
        <div className="h-3 bg-gray-200 rounded w-[110px]" />
      </div>
    ))}
  </div>
);

// --- Processing screen shown while the requirement analysis is polling ---
// `showLeaveHint` is true only during an actual run — not when we're just
// loading already-finished results, where "you can leave" would be misleading.
// `documents` (when the run has them) adds one progress bar per file, with
// Resume on any that stopped short (`onResume`).
const RequirementAnalysisProcessingScreen = ({ showLeaveHint = true, documents, onResume }) => {
  const { total, done } = summarize(documents);
  return (
    <div className="relative flex-1 h-full w-full overflow-hidden font-outfit bg-[#fafafa]">
      <ReqBackgroundPreview />
      <div aria-hidden="true" className="absolute inset-0 bg-black opacity-[0.26]" />
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <div
          className={`relative w-full ${total > 0 ? "max-w-[520px]" : "max-w-[400px]"} bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-100 px-6 py-8 text-center`}
        >
          <div className="mx-auto mb-4 w-10 h-10 rounded-full border-[3px] border-[#E9D5FF] border-t-[#8B5CF6] animate-spin" />
          <h2 className="font-outfit text-[16px] font-semibold text-[#3F3F46]">Analyzing requirements</h2>
          <p className="mt-2 font-outfit text-[13px] text-[#767676]">
            Extracting requirements, checking alignment, and matching evidence. This may take a moment.
          </p>
          {total > 0 && (
            <div className="mt-5 text-left">
              <p className="font-outfit text-[12px] font-medium text-[#71717A]">
                {done} of {total} file{total === 1 ? "" : "s"} done
              </p>
              <ExtractionProgressList
                documents={documents}
                onResume={onResume}
                className="mt-1 max-h-[40vh] overflow-y-auto pr-1 custom-scrollbar"
              />
            </div>
          )}
          {showLeaveHint && (
            <p className="mt-3 font-outfit text-[12px] text-[#9A9A9A]">
              You can leave this page, we'll keep working and notify you when it's ready.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Initial "Requirement Analysis" screen shown before extraction runs ---
// Also where a run that produced nothing lands (every file stopped short), so
// when any file did, its line is shown here with Resume (`documents`, `onResume`).
const RequirementAnalysisInitialScreen = ({ onRun, isAnalyzing, documents, onResume }) => {
  const stopped = summarize(documents).needsAction;
  return (
  <div className="relative flex-1 h-full w-full overflow-hidden font-outfit bg-[#fafafa]">
    <ReqBackgroundPreview />
    <div aria-hidden="true" className="absolute inset-0 bg-black opacity-[0.26]" />
    <div className="absolute inset-0 flex items-center justify-center px-4">
      <div
        className={`relative w-full ${stopped.length ? "max-w-[520px]" : "max-w-[400px]"} bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-100 px-5 py-6`}
      >
        <h2 className="text-center font-outfit text-[16px] font-semibold leading-[normal] text-[#3F3F46]">
          Requirement Analysis
        </h2>
        <p className="mt-2 text-center font-outfit text-[13px] font-normal leading-[normal] text-[#767676]">
          Run requirement extraction to identify project requirements from uploaded documents,
          organize them by category, and prepare them for assignment and review.
        </p>

        <div className="border-t border-[#EFEDF1] my-5" />

        <div className="flex flex-col gap-4">
          {REQ_CHECKLIST.map((label) => (
            <div key={label} className="flex items-center gap-3">
              <ReqCheckCircleIcon />
              <span className="font-outfit text-[14px] font-normal leading-[normal] text-[#52525B]">
                {label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-7">
          <button
            type="button"
            onClick={onRun}
            disabled={isAnalyzing}
            className="inline-flex items-center gap-2 text-white text-[15px] font-medium px-5 py-2.5 transition-opacity hover:opacity-95 disabled:opacity-60 rounded-[6px] bg-[linear-gradient(270deg,rgba(99,102,241,0.94)_0%,rgba(139,92,246,0.94)_100%)] shadow-[0_1px_2px_0_rgba(0,0,0,0.30),0_1px_3px_1px_rgba(0,0,0,0.15)]"
          >
            <ReqSparkleIcon />
            {isAnalyzing ? "Analyzing…" : "Run Analysis"}
          </button>
        </div>

        {stopped.length > 0 && (
          <div className="mt-6 border-t border-[#EFEDF1] pt-4">
            <p className="font-outfit text-[12px] font-medium text-[#B91C1C]">
              The last run stopped short on {stopped.length} file{stopped.length === 1 ? "" : "s"}.
              Resume continues from where each stopped.
            </p>
            <ExtractionProgressList
              documents={stopped}
              onResume={onResume}
              className="mt-1 max-h-[30vh] overflow-y-auto pr-1 custom-scrollbar"
            />
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

// --- Status pill for the requirements table ---
const ReqStatusBadge = ({ aligned }) =>
  aligned ? (
    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full border border-[#258F40B2] text-[#258F40] bg-[#EBF7ED] text-[13px] font-medium whitespace-nowrap">
      Aligned
    </span>
  ) : (
    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full border border-[#CCAB29B2] text-[#CCAB29] bg-[#FCF9ED] text-[13px] font-medium whitespace-nowrap">
      Not Aligned
    </span>
  );

const ReqSourceLink = ({ disabled, onClick, label = "View Source" }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex items-center text-[13px] font-medium underline underline-offset-2 ${
      disabled ? "text-[#A04A97] opacity-40 cursor-not-allowed" : "text-[#A04A97]"
    }`}
  >
    {label}
  </button>
);

// File-type icon for matched-evidence rows.
const getEvidenceIcon = (fileName = "") => {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  switch (ext) {
    case "pdf":
      return "/pdff.svg";
    case "xlsx":
    case "xls":
      return "/Xls.svg";
    case "docx":
    case "doc":
      return "/docX.svg";
    case "csv":
      return "/CSV.svg";
    case "txt":
      return "/TXT.svg";
    default:
      return "/pdf.svg";
  }
};

// Fixed display order for the requirement category sections. These are the
// LLM-extracted categories, always rendered (even at 0). "Custom Requirement"
// is intentionally NOT here — it's user-added only, so it surfaces as an extra
// section solely when at least one manual requirement exists.
const CATEGORY_ORDER = [
  "Technical Requirements",
  "Deliverables & Timeline",
  "Evaluation Criteria",
  "Compliance & Legal",
];

// Per-category header colors (bg / border / text). The count badge is always
// white with a #CCCCCC border and the category's text color. Unknown categories
// fall back to the neutral gray style.
const CATEGORY_CONFIG = {
  "Technical Requirements": { bg: "#0369A10D", border: "#0000001A", text: "#0369A1" },
  "Deliverables & Timeline": { bg: "#4587640D", border: "#458764", text: "#458764" },
  "Evaluation Criteria": { bg: "#B7791F0D", border: "#B7791F", text: "#B7791F" },
  "Compliance & Legal": { bg: "#8A38F50D", border: "#8A38F5", text: "#8A38F5" },
  "Custom Requirement": { bg: "#B7B7B70D", border: "#B7B7B7", text: "#52525B" },
};
const DEFAULT_CATEGORY_CONFIG = { bg: "#B7B7B70D", border: "#B7B7B7", text: "#52525B" };
const getCategoryConfig = (cat) => CATEGORY_CONFIG[cat] || DEFAULT_CATEGORY_CONFIG;

// Build the section list in fixed order, ALWAYS including the known categories
// (with an empty array when absent so they still render as "0 Requirements"),
// followed by any extra categories present in the data.
const buildOrderedSections = (groups) => {
  const known = CATEGORY_ORDER.map((cat) => [cat, groups[cat] || []]);
  const extra = Object.entries(groups)
    .filter(([cat]) => !CATEGORY_ORDER.includes(cat))
    .sort(([a], [b]) => a.localeCompare(b));
  return [...known, ...extra];
};

// Group a flat list by the RFP heading each item came from (`section`), in the
// order the headings first appear — which is the order of the document. Items with
// no heading collect in one trailing group. Returns [] when there is nothing worth
// grouping (no item has a heading, or all share one), so callers fall back to the
// plain list instead of showing a single header over everything.
const OTHER_SECTION = "Other requirements";
const groupBySection = (items) => {
  const order = [];
  const byHeading = new Map();
  const loose = [];
  (items || []).forEach((it) => {
    const head = (it.section || "").trim();
    if (!head) {
      loose.push(it);
      return;
    }
    if (!byHeading.has(head)) {
      byHeading.set(head, []);
      order.push(head);
    }
    byHeading.get(head).push(it);
  });
  if (order.length < 2) return [];
  const groups = order.map((head) => ({ head, items: byHeading.get(head) }));
  if (loose.length) groups.push({ head: OTHER_SECTION, items: loose });
  return groups;
};

// Shared 6-column grid template (Description | Source | Assigned | Status | Evidence | Actions).
const REQ_GRID = "minmax(0,1fr) 110px 150px 140px 200px 70px";

// Interactive "Assigned To" control — click to open a member picker and assign
// the requirement. The dropdown is portaled to the body so the row/section
// overflow containers don't clip it.
const AssignedToCell = ({ assignedIds, members, isOpen, loading, onToggleOpen, onSelect }) => {
  const btnRef = React.useRef(null);
  const [coords, setCoords] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    setQuery(""); // reset search each time it opens
    // Reposition on every scroll/resize so the portaled dropdown follows the
    // trigger (it lives in a scrolling table), and flip it above the trigger
    // when there isn't enough room below (bottom rows).
    const place = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      const DROPDOWN_H = 210;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < DROPDOWN_H && r.top > spaceBelow;
      setCoords({
        left: r.left,
        width: r.width,
        top: openUp ? null : r.bottom + 4,
        bottom: openUp ? window.innerHeight - r.top + 4 : null,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isOpen]);

  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members || [];
    return (members || []).filter(
      (m) =>
        (m.full_name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
    );
  }, [members, query]);

  const nameById = useMemo(() => {
    const m = {};
    (members || []).forEach((x) => {
      m[x.user_id] = x.full_name || x.email || `User ${x.user_id}`;
    });
    return m;
  }, [members]);

  const ids = assignedIds || [];
  const label =
    ids.length === 0
      ? "Unassigned"
      : ids.length === 1
      ? nameById[ids[0]] || "1 member"
      : `${nameById[ids[0]] || "Member"} +${ids.length - 1}`;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={loading}
        onClick={(e) => {
          e.stopPropagation();
          onToggleOpen();
        }}
        className={`inline-flex items-center justify-between gap-2 w-full max-w-[150px] px-2.5 py-1.5 rounded-md border bg-white text-[13px] text-[#52525B] transition ${
          loading ? "opacity-70 cursor-wait border-[#0000001F]" : "border-[#0000001F] hover:border-[#C95EBE] cursor-pointer"
        }`}
      >
        <span className="truncate">{loading ? "Saving…" : label}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`opacity-60 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen &&
        coords &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: coords.left,
              width: Math.max(coords.width, 170),
              zIndex: 9999,
              ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }),
            }}
            className="bg-white border border-[#0000001A] rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.12)] font-outfit flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-1.5 border-b border-[#0000000D]">
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search members..."
                style={{ fontWeight: 400 }}
                className="w-full px-2 py-1 text-[13px] border border-[#0000001F] rounded focus:outline-none focus:border-[#C95EBE] transition"
              />
            </div>
            <div className="max-h-[140px] overflow-auto custom-scrollbar py-1">
              {filteredMembers.length === 0 ? (
                <div className="px-3 py-2 text-[13px] text-gray-400">
                  {(members || []).length === 0 ? "No members available" : "No members found"}
                </div>
              ) : (
                filteredMembers.map((m) => {
                  const selected = ids.includes(m.user_id);
                  const role = m.permission_role || m.role;
                  return (
                    <button
                      key={m.user_id}
                      type="button"
                      onClick={() => onSelect(m.user_id)}
                      className="w-full text-left px-3 py-1.5 hover:bg-[#F3F4F6] flex items-center justify-between gap-2"
                    >
                      <span className="truncate text-[13px] text-[#3F3F46]">
                        {m.full_name || m.email}
                        {role && <span className="text-[#BB42AE] ml-1">({role})</span>}
                      </span>
                      {selected && <span className="text-[#BB42AE] flex-shrink-0">✓</span>}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// Interactive "Status" pill — click to open a dropdown and override the
// requirement's Aligned / Not Aligned state. The dropdown is portaled to the
// body so the row/section overflow containers don't clip it.
const StatusCell = ({ aligned, isOpen, loading, disabled, onToggleOpen, onSelect }) => {
  const btnRef = React.useRef(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    // Follow the trigger on scroll (the table scrolls) and flip above it when
    // there isn't room below (bottom rows), so the dropdown never clips.
    const place = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      const DROPDOWN_H = 96;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < DROPDOWN_H && r.top > spaceBelow;
      setCoords({
        left: r.left + r.width / 2,
        top: openUp ? null : r.bottom + 4,
        bottom: openUp ? window.innerHeight - r.top + 4 : null,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isOpen]);

  let cls = "bg-white text-[#9CA3AF] border-[#D1D5DB]";
  let label = "Set Status";
  if (aligned === true) {
    cls = "bg-[#D5FCCF] text-[#23853B] border-[#198808]";
    label = "Aligned";
  } else if (aligned === false) {
    cls = "bg-[#FDF6E3] text-[#BD9700] border-[#BD9700]";
    label = "Not Aligned";
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={loading || disabled}
        title={disabled ? "Status can't be changed while analysis is running" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          onToggleOpen();
        }}
        className={`inline-flex items-center justify-center gap-1 w-[120px] py-1 rounded-full border text-[13px] font-medium whitespace-nowrap transition ${cls} ${
          loading
            ? "opacity-70 cursor-wait"
            : disabled
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:brightness-[0.97]"
        }`}
      >
        {loading ? "Saving…" : label}
        {!disabled && (
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`opacity-70 transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {!disabled &&
        isOpen &&
        coords &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: coords.left,
              transform: "translateX(-50%)",
              zIndex: 9999,
              ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }),
            }}
            className="w-[130px] bg-white border border-[#0000001A] rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.12)] p-1 font-outfit"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => onSelect(true)}
              className="w-full text-left px-3 py-1.5 text-[13px] font-medium text-[#23853B] hover:bg-[#F3F4F6] rounded flex items-center justify-between"
            >
              Aligned {aligned === true && "✓"}
            </button>
            <button
              type="button"
              onClick={() => onSelect(false)}
              className="w-full text-left px-3 py-1.5 text-[13px] font-medium text-[#BD9700] hover:bg-[#F3F4F6] rounded flex items-center justify-between mt-0.5"
            >
              Not Aligned {aligned === false && "✓"}
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

// Row "⋯" actions menu (currently just Delete). Portaled to the body so the
// row/section overflow containers don't clip it; flips above the trigger when
// there isn't enough room below (bottom rows). Clicking Delete swaps the menu
// to an inline confirmation step, anchored under the same button.
const RowActionsMenu = ({ isOpen, onToggleOpen, onDelete, deleting }) => {
  const btnRef = React.useRef(null);
  const [coords, setCoords] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Reset to the menu (non-confirming) state whenever the popover closes.
  useEffect(() => {
    if (!isOpen) setConfirming(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    const place = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      // Tall enough to cover the confirmation step so the flip check holds.
      const MENU_H = 120;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < MENU_H && r.top > spaceBelow;
      setCoords({
        right: window.innerWidth - r.right,
        top: openUp ? null : r.bottom + 4,
        bottom: openUp ? window.innerHeight - r.top + 4 : null,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleOpen();
        }}
        className="w-8 h-8 rounded-md flex items-center justify-center text-[#A259FF] hover:bg-[#A259FF]/10 transition-colors"
        aria-label="Row actions"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>

      {isOpen &&
        coords &&
        createPortal(
          <div
            style={{
              position: "fixed",
              right: coords.right,
              zIndex: 9999,
              ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }),
            }}
            className="bg-white border border-[#0000001A] rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.12)] font-outfit"
            onClick={(e) => e.stopPropagation()}
          >
            {!confirming ? (
              <div className="py-1 min-w-[150px]">
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="w-full text-left px-3 py-2 text-[13px] font-medium text-[#DC2626] hover:bg-[#FEF2F2] flex items-center gap-2"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                  Delete
                </button>
              </div>
            ) : (
              <div className="p-3 w-[230px]">
                <p className="text-[13px] text-[#52525B] leading-snug">
                  Delete this requirement? This can’t be undone.
                </p>
                <div className="flex items-center justify-end gap-2 mt-3">
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => setConfirming(false)}
                    className="px-3 py-1.5 rounded-md border border-[#0000001F] text-[#52525B] text-[13px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={onDelete}
                    className="px-3 py-1.5 rounded-md bg-[#DC2626] text-white text-[13px] font-medium hover:bg-[#B91C1C] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {deleting ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

// --- New requirements results table — maps the alignment API data ---
const RequirementsResultView = ({
  data,
  folderId,
  onRefresh,
  onGenerateScreening,
  screeningAnalyzing,
  analysisRunning = false,
  onResume,
}) => {
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [search, setSearch] = useState("");
  // Accordion behavior: at most one category section open at a time.
  // `null` means every section is collapsed (the default state).
  const [expandedCategory, setExpandedCategory] = useState(null);
  const categorySectionRefs = useRef({});
  // Scope of Work sections the user has folded away, keyed by heading.
  const [collapsedScope, setCollapsedScope] = useState({});
  // Per-category scroll tracking for the rows list — drives the floating
  // "jump to top" arrow that appears once a section is scrolled down.
  const rowsScrollRefs = useRef({});
  const [scrolledCategories, setScrolledCategories] = useState({});

  const handleToggleCategory = (cat) => {
    setExpandedCategory((prev) => {
      const next = prev === cat ? null : cat;
      if (next) {
        // Bring the newly-expanded section to the top of the scroll area
        // instead of leaving it wherever it happened to land.
        requestAnimationFrame(() => {
          categorySectionRefs.current[cat]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
      return next;
    });
  };

  const handleRowsScroll = (cat, e) => {
    const isScrolled = e.currentTarget.scrollTop > 24;
    setScrolledCategories((prev) =>
      !!prev[cat] === isScrolled ? prev : { ...prev, [cat]: isScrolled }
    );
  };

  const scrollCategoryRowsToTop = (cat) => {
    rowsScrollRefs.current[cat]?.scrollTo({ top: 0, behavior: "smooth" });
  };
  const [isSourceOpen, setIsSourceOpen] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [doneOverrides, setDoneOverrides] = useState({});
  const [togglingIds, setTogglingIds] = useState({});
  const [alignmentDropdownId, setAlignmentDropdownId] = useState(null);
  const [alignedOverrides, setAlignedOverrides] = useState({});
  const [togglingAlign, setTogglingAlign] = useState({});
  const [members, setMembers] = useState([]);
  const [assignDropdownId, setAssignDropdownId] = useState(null);
  const [assignOverrides, setAssignOverrides] = useState({});
  const [assigningId, setAssigningId] = useState({});
  const [addReqOpen, setAddReqOpen] = useState(false);
  const [addingReq, setAddingReq] = useState(false);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [deletingId, setDeletingId] = useState({});

  // org_id lives in the route (/project/:folderId/org/:orgId); needed by the
  // member-list + assign endpoints.
  const orgId = useMemo(() => {
    const m = window.location.pathname.match(/\/project\/\d+\/org\/(\d+)/);
    return m ? m[1] : null;
  }, []);

  const screeningStatus = data?.screening_status || "not_started";
  // "Generating" while either the background job is active OR the backend still
  // reports the screening task running (covers a reload mid-run before the job
  // store rehydrates, and the brief window after the job ends but before refetch).
  const isGenerating = !!screeningAnalyzing || screeningStatus === "running";

  // Kick off screening as a background job (driven by AnalysisWatcherHost), so it
  // keeps generating after navigation and toasts on completion. Polling/refresh
  // is handled by ProjectPage while the job runs — nothing to poll here.
  const handleGenerateAnswers = () => {
    if (!folderId || isGenerating) return;
    onGenerateScreening?.();
  };

  const openSource = (src) => {
    if (!src?.document_url) return;
    setSourceData(src);
    setIsSourceOpen(true);
  };

  // Screening rows carry their evidence in sources[]; promote the first source
  // to the document fields the viewer expects (matches the original behavior).
  const handleScreeningViewSource = (item, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    let processed = { ...item };
    if (item?.sources && item.sources.length > 0) {
      const top = item.sources[0];
      processed = {
        ...processed,
        document_url: top.file_url,
        url: top.file_url,
        document_name: top.file_name,
        name: top.file_name,
        source_text: top.snippet,
        highlightText: top.snippet,
        page_number: top.page,
        page: top.page,
      };
    }
    setSourceData(processed);
    if (processed.document_url) setIsSourceOpen(true);
  };

  const groups = useMemo(() => {
    const out = {};
    const q = search.trim().toLowerCase();
    (data?.documents || []).forEach((doc) => {
      const cats = doc.requirements_by_category || {};
      Object.entries(cats).forEach(([cat, reqs]) => {
        // Scope of Work lives in its own dedicated sub-tab, not here.
        if (cat === "Scope of Work") return;
        (reqs || []).forEach((r) => {
          if (q && !(r.text || "").toLowerCase().includes(q)) return;
          (out[cat] = out[cat] || []).push({ ...r, doc_name: doc.doc_name, doc_id: doc.doc_id });
        });
      });
    });
    return out;
  }, [data, search]);

  const screening = useMemo(() => {
    const qs = data?.screening_questions || [];
    const q = search.trim().toLowerCase();
    return q ? qs.filter((x) => (x.text || "").toLowerCase().includes(q)) : qs;
  }, [data, search]);

  const totalReqs = useMemo(
    () => Object.values(groups).reduce((n, arr) => n + arr.length, 0),
    [groups]
  );

  // Scope-of-Work items flattened across documents, carrying parent doc_id
  // (needed by the mark-done endpoint).
  const scopeItems = useMemo(() => {
    const out = [];
    const q = search.trim().toLowerCase();
    (data?.documents || []).forEach((doc) => {
      const items = (doc.requirements_by_category || {})["Scope of Work"] || [];
      items.forEach((r) => {
        if (q && !(r.text || "").toLowerCase().includes(q)) return;
        out.push({ ...r, doc_id: doc.doc_id, doc_name: doc.doc_name });
      });
    });
    return out;
  }, [data, search]);

  // Optimistic toggle + persist via the scope-of-work mark-done API.
  const handleToggleScopeDone = async (item) => {
    if (!folderId || !item.id) return;
    const id = item.id;
    const current = doneOverrides[id] ?? item.done;
    const next = !current;
    setDoneOverrides((p) => ({ ...p, [id]: next }));
    setTogglingIds((p) => ({ ...p, [id]: true }));
    try {
      await api.post(
        `/api/v1/rfp/folders/${folderId}/insights/scope-of-work/mark-done/`,
        { doc_id: item.doc_id, requirement_id: id, done: next }
      );
    } catch (e) {
      setDoneOverrides((p) => ({ ...p, [id]: current })); // revert
      toast.error("Failed to update scope item");
    } finally {
      setTogglingIds((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }
  };

  // Manual Aligned / Not-Aligned override — optimistic, persisted via the
  // alignment toggle endpoint, then refreshed so the aggregate scores stay in sync.
  const handleToggleAlignment = async (item, newAligned) => {
    if (!folderId || !item.id) return;
    // The backend toggle requires alignment_status === "done"; block it while a
    // (re-)analysis is running so it can't fail with a confusing error.
    if (analysisRunning) {
      setAlignmentDropdownId(null);
      return;
    }
    setAlignmentDropdownId(null);
    const id = item.id;
    const current = alignedOverrides[id] ?? item.aligned;
    if (current === newAligned) return;

    setAlignedOverrides((p) => ({ ...p, [id]: newAligned }));
    setTogglingAlign((p) => ({ ...p, [id]: true }));
    try {
      await api.post(
        `/api/v1/rfp/folders/${folderId}/requirements/alignment/toggle/`,
        { doc_id: item.doc_id, requirement_id: id, aligned: newAligned }
      );
      toast.success(`Marked as ${newAligned ? "Aligned" : "Not Aligned"}`);
      if (onRefresh) await onRefresh();
      // Fresh data is now authoritative — drop the optimistic override.
      setAlignedOverrides((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    } catch (e) {
      // Revert the optimistic change back to the server value.
      setAlignedOverrides((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
      toast.error("Failed to update status");
    } finally {
      setTogglingAlign((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }
  };

  // Close the status dropdown on any outside click.
  useEffect(() => {
    if (alignmentDropdownId == null) return;
    const onDocClick = () => setAlignmentDropdownId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [alignmentDropdownId]);

  // Fetch the folder's org members once, for the Assigned-To picker.
  useEffect(() => {
    if (!folderId || !orgId) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(
          `/api/v1/rfp/folders/${folderId}/section-view/members/list/?organization_id=${orgId}&include_org_members=true`
        );
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
        if (alive) setMembers(list);
      } catch (e) {
        // Non-fatal: the picker just shows "No members available".
      }
    })();
    return () => {
      alive = false;
    };
  }, [folderId, orgId]);

  // Current assignee ids for a requirement: optimistic override wins, else read
  // whatever the API surfaced (ids or member objects).
  const getAssignedIds = (item) => {
    if (assignOverrides[item.id]) return assignOverrides[item.id];
    const raw = item.assigned_member_ids || item.assigned_members || [];
    return raw
      .map((x) => (typeof x === "object" ? x.id ?? x.user_id : x))
      .filter((x) => x != null);
  };

  // Assign a member to a requirement (additive on the backend), with optimistic
  // UI and revert on failure.
  const handleAssign = async (item, userId) => {
    if (!folderId || !orgId || !item.id) return;
    const id = item.id;
    const prev = getAssignedIds(item);
    setAssignDropdownId(null);
    setAssignOverrides((p) => ({ ...p, [id]: [userId] }));
    setAssigningId((p) => ({ ...p, [id]: true }));
    try {
      const res = await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/assign-members/?requirement=true`,
        {
          org_id: Number(orgId),
          requirement_id: id,
          doc_id: item.doc_id,
          user_ids: [userId],
        }
      );
      const assigned = res.data?.assigned_member_ids;
      setAssignOverrides((p) => ({
        ...p,
        [id]: Array.isArray(assigned) ? assigned : [userId],
      }));
      toast.success("Member assigned");
    } catch (e) {
      setAssignOverrides((p) => ({ ...p, [id]: prev })); // revert
      toast.error("Failed to assign member");
    } finally {
      setAssigningId((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }
  };

  // Close the assignee dropdown on any outside click.
  useEffect(() => {
    if (assignDropdownId == null) return;
    const onDocClick = () => setAssignDropdownId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [assignDropdownId]);

  // Close the row actions menu on any outside click.
  useEffect(() => {
    if (actionMenuId == null) return;
    const onDocClick = () => setActionMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [actionMenuId]);

  // Delete a requirement (manual or extracted), then refresh so the row drops
  // out of its category section.
  const handleDeleteRequirement = async (item) => {
    if (!folderId || !item?.id || !item?.doc_id) return;
    const id = item.id;
    setDeletingId((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/api/v1/rfp/insights/requirements/delete/`, {
        folder_id: folderId,
        doc_id: item.doc_id,
        requirement_id: id,
      });
      toast.success("Requirement deleted");
      setActionMenuId(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error("Failed to delete requirement");
    } finally {
      setDeletingId((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }
  };

  // Add a custom requirement, then refresh so it appears in its category section.
  const handleAddRequirement = async (category, text) => {
    if (!folderId || !category || !text) return;
    setAddingReq(true);
    try {
      await api.post(`/api/v1/rfp/insights/requirements/add/`, {
        folder_id: folderId,
        category,
        text,
      });
      toast.success("Requirement added");
      setAddReqOpen(false);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error("Failed to add requirement");
    } finally {
      setAddingReq(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full overflow-hidden bg-[#FAF9FB] px-4 py-3 font-outfit">
      {/* Per-file extraction progress: requirements from finished files are in
          the table below; this keeps the rest — still extracting, or stopped
          and waiting for Resume — in view. */}
      <ExtractionProgressPanel documents={data?.documents} running={analysisRunning} onResume={onResume} />

      {/* Sub-tabs + search */}
      <div className="flex items-center justify-between  flex-shrink-0">
        <div className="flex items-center w-max border-l border-r border-t border-[#0000001A] rounded-t-lg overflow-hidden">
          <button
            onClick={() => setActiveSubTab("all")}
            className={`px-5 py-2 text-[14px] font-medium transition-colors border-r border-[#0000001A] ${
              activeSubTab === "all" ? "bg-[#F7EBF6] text-[#BB42AE]" : "bg-white text-[#707070] hover:bg-gray-50"
            }`}
          >
            All Requirements
          </button>
          <button
            onClick={() => setActiveSubTab("scope")}
            className={`px-5 py-2 text-[14px] font-medium transition-colors border-r border-[#0000001A] ${
              activeSubTab === "scope" ? "bg-[#F7EBF6] text-[#BB42AE]" : "bg-white text-[#707070] hover:bg-gray-50"
            }`}
          >
            Scope of Work
          </button>
          <button
            onClick={() => setActiveSubTab("screening")}
            className={`px-5 py-2 text-[14px] font-medium transition-colors ${
              activeSubTab === "screening" ? "bg-[#F7EBF6] text-[#BB42AE]" : "bg-white text-[#707070] hover:bg-gray-50"
            }`}
          >
            Screening Questions
          </button>
        </div>
        <div className="flex items-center gap-3 rounded-sm -translate-y-1.5">
          {activeSubTab !== "scope" && (
            <div className="relative w-[280px] ">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <input
                type="text"
                placeholder={
                  activeSubTab === "screening" ? "Search Questions..." : "Search Requirements..."
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ fontWeight: 400 }}
                className="w-full pl-9 pr-4 py-1.5 border border-[#00000036] rounded text-[14px] font-outfit focus:outline-none focus:border-[#C95EBE] transition h-[38px]"
              />
            </div>
          )}

          {activeSubTab === "all" && (
            <button
              onClick={() => setAddReqOpen(true)}
              className="flex items-center gap-1.5 h-[38px] px-3 rounded border border-[#DEDEDE] text-[#A04A97] text-[14px] font-medium hover:bg-[#FBF0F9] transition-colors whitespace-nowrap bg-[#FFFFFF]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M6 8H1C0.716667 8 0.479167 7.90417 0.2875 7.7125C0.0958333 7.52083 0 7.28333 0 7C0 6.71667 0.0958333 6.47917 0.2875 6.2875C0.479167 6.09583 0.716667 6 1 6H6V1C6 0.716667 6.09583 0.479167 6.2875 0.2875C6.47917 0.0958333 6.71667 0 7 0C7.28333 0 7.52083 0.0958333 7.7125 0.2875C7.90417 0.479167 8 0.716667 8 1V6H13C13.2833 6 13.5208 6.09583 13.7125 6.2875C13.9042 6.47917 14 6.71667 14 7C14 7.28333 13.9042 7.52083 13.7125 7.7125C13.5208 7.90417 13.2833 8 13 8H8V13C8 13.2833 7.90417 13.5208 7.7125 13.7125C7.52083 13.9042 7.28333 14 7 14C6.71667 14 6.47917 13.9042 6.2875 13.7125C6.09583 13.5208 6 13.2833 6 13V8Z" fill="#A04A97"/>
</svg>
              Add Requirement
            </button>
          )}

          {activeSubTab === "screening" && screeningStatus !== "done" && (
            <button
              onClick={handleGenerateAnswers}
              disabled={isGenerating || screeningStatus === "running"}
              className={`flex items-center gap-2 bg-gradient-to-br from-[#6366F1] to-[#8B55DD] text-white px-3 py-1 rounded-md font-medium text-[14px] h-[38px] transition-colors ${
                isGenerating || screeningStatus === "running"
                  ? "opacity-75 cursor-not-allowed"
                  : "opacity-[0.94] hover:opacity-100 shadow-sm"
              }`}
            >
              {isGenerating || screeningStatus === "running" ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <SparklingIcon />
                  Generate Answers
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {activeSubTab === "all" ? (
        <div className="flex-1 overflow-auto border border-[#0000001A] rounded-b-lg custom-scrollbar bg-white flex flex-col">
          {/* Sticky column header */}
          <div
            className="sticky top-0 z-10 grid bg-[#FAFAFA] text-[13px] text-[#707070]"
            style={{ gridTemplateColumns: REQ_GRID }}
          >
            <div className="px-4 py-3 font-medium">Requirement Description</div>
            <div className="px-4 py-3 font-medium">RFP Source</div>
            <div className="px-4 py-3 font-medium">Assigned To</div>
            <div className="px-4 py-3 font-medium">Status</div>
            <div className="px-4 py-3 font-medium">Matched Evidence</div>
            <div className="px-4 py-3 font-medium text-center">Actions</div>
          </div>

          {totalReqs === 0 ? (
            <div className="px-4 py-10 text-center text-gray-400 italic text-[14px]">
              No requirements found.
            </div>
          ) : (
            buildOrderedSections(groups).map(([cat, reqs]) => {
              const isCollapsed = expandedCategory !== cat;
              const cfg = getCategoryConfig(cat);
              // Expanded shows ~5 rows then scrolls; collapsed hides rows entirely.
              const ROW_H = 64;
              const maxBodyHeight = 5 * ROW_H;

              return (
                <div
                  key={cat}
                  ref={(el) => {
                    if (el) categorySectionRefs.current[cat] = el;
                    else delete categorySectionRefs.current[cat];
                  }}
                  className="mb-3 last:mb-0 border-y border-[#0000001A]"
                  style={{
                    borderTopWidth: "1.5px",
                    borderBottomWidth: "1.5px",
                    // Keep the section clear of the sticky column header above it
                    // when it scrolls into view.
                    scrollMarginTop: 44,
                  }}
                >
                  {/* Category header */}
                  <button
                    type="button"
                    onClick={() => handleToggleCategory(cat)}
                    className="w-full flex items-center justify-between px-4 transition-colors hover:brightness-[0.98]"
                    style={{ height: "45px", backgroundColor: cfg.bg }}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="font-semibold text-[15px]" style={{ color: cfg.text }}>
                        {cat}
                      </span>
                      <span
                        className="text-[12px] rounded px-2 py-0.5"
                        style={{ color: cfg.text, backgroundColor: "#FFFFFF", border: "1px solid #CCCCCC" }}
                      >
                        {reqs.length} Requirements
                      </span>
                    </div>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`text-[#707070] transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {/* Scrollable rows for this section — hidden entirely when collapsed */}
                  {!isCollapsed && (
                  <div className="relative">
                  <div
                    ref={(el) => {
                      if (el) rowsScrollRefs.current[cat] = el;
                      else delete rowsScrollRefs.current[cat];
                    }}
                    onScroll={(e) => handleRowsScroll(cat, e)}
                    className="overflow-y-auto custom-scrollbar"
                    style={{ maxHeight: maxBodyHeight }}
                  >
                    {reqs.map((r, i) => {
                      // Show every matched evidence doc. Some carry a viewable
                      // URL (clickable), others only carry a name + snippet,
                      // which we still surface as text.
                      const evidenceDocs = r.source_docs || [];
                      return (
                        <div
                          key={r.id || i}
                          className="grid border-t border-[#00000014] hover:bg-[#F8F3FF] transition-colors"
                          style={{ gridTemplateColumns: REQ_GRID }}
                        >
                          <div className="px-4 py-3 flex items-center font-outfit text-[14px] font-medium leading-[18px] text-[#52525B] border-r border-[#00000014]">{r.text}</div>
                          <div className="px-4 py-3 flex items-center border-r border-[#00000014]">
                            {r.source === "user" ? (
                              // Manually-added requirements have no real RFP
                              // source — show a dash instead of View Source.
                              <span className="text-[#9CA3AF] font-outfit text-[14px]">—</span>
                            ) : (
                              <ReqSourceLink
                                disabled={!r.document_url}
                                onClick={() =>
                                  openSource({
                                    document_url: r.document_url,
                                    document_name: r.document_name || r.doc_name,
                                    page_number: r.page_number,
                                    source_text: r.source_text,
                                  })
                                }
                              />
                            )}
                          </div>
                          <div className="px-4 py-3 flex items-center border-r border-[#00000014]">
                            <AssignedToCell
                              assignedIds={getAssignedIds(r)}
                              members={members}
                              isOpen={assignDropdownId === r.id}
                              loading={!!assigningId[r.id]}
                              onToggleOpen={() =>
                                setAssignDropdownId(assignDropdownId === r.id ? null : r.id)
                              }
                              onSelect={(userId) => handleAssign(r, userId)}
                            />
                          </div>
                          <div className="px-4 py-3 flex items-center border-r border-[#00000014]">
                            {analysisRunning && (alignedOverrides[r.id] ?? r.aligned) === undefined ? (
                              <span className="inline-flex items-center gap-1.5 text-[13px] text-[#9CA3AF] italic">
                                <span className="w-3 h-3 border-2 border-[#9CA3AF] border-t-transparent rounded-full animate-spin" />
                                Analysing…
                              </span>
                            ) : (
                              <StatusCell
                                aligned={alignedOverrides[r.id] ?? r.aligned}
                                isOpen={alignmentDropdownId === r.id}
                                loading={!!togglingAlign[r.id]}
                                disabled={analysisRunning}
                                onToggleOpen={() =>
                                  setAlignmentDropdownId(alignmentDropdownId === r.id ? null : r.id)
                                }
                                onSelect={(val) => handleToggleAlignment(r, val)}
                              />
                            )}
                          </div>
                          <div className="px-4 py-3 flex items-center border-r border-[#00000014]">
                            {evidenceDocs.length > 0 ? (
                              <div className="flex flex-col gap-1.5 w-full">
                                {evidenceDocs.map((s, di) => {
                                  const sName = s.doc_name || s.file_name || s.name || "Document";
                                  const sUrl = s.file_url || s.url || s.document_url || "";
                                  const sSnippet = s.snippet || s.source_text || "";
                                  return (
                                    <button
                                      key={di}
                                      type="button"
                                      title={sSnippet || sName}
                                      onClick={() =>
                                        openSource({
                                          document_url: sUrl,
                                          document_name: sName,
                                          page_number: s.page || s.page_number,
                                          source_text: sSnippet,
                                        })
                                      }
                                      className="inline-flex items-center gap-1.5 text-[13px] text-[#52525B] underline underline-offset-2 max-w-[220px]"
                                    >
                                      <img
                                        src={getEvidenceIcon(sName)}
                                        alt=""
                                        className="w-[18px] h-[18px] object-contain flex-shrink-0"
                                      />
                                      <span className="truncate">{sName}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </div>
                          <div className="px-2 py-3 flex items-center justify-center">
                            <RowActionsMenu
                              isOpen={actionMenuId === r.id}
                              onToggleOpen={() =>
                                setActionMenuId(actionMenuId === r.id ? null : r.id)
                              }
                              onDelete={() => handleDeleteRequirement(r)}
                              deleting={!!deletingId[r.id]}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {scrolledCategories[cat] && (
                    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-3 z-20">
                      <button
                        type="button"
                        onClick={() => scrollCategoryRowsToTop(cat)}
                        aria-label={`Scroll to top of ${cat}`}
                        className="bg-gradient-to-br from-[#C95EBE] to-[#BD4AB1] text-white rounded-full p-2 shadow-2xl hover:shadow-xl transition-all duration-200 hover:scale-110"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 19V5M5 12l7-7 7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                  </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ) : activeSubTab === "scope" ? (
        <div className="flex-1 overflow-auto border border-[#0000001A] rounded-b-lg custom-scrollbar bg-white">
          {scopeItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-gray-400 italic text-[14px]">
              No scope of work items found.
            </div>
          ) : (
            (() => {
              const renderScopeItem = (item, i) => {
                const checked = doneOverrides[item.id] ?? item.done;
                const busy = !!togglingIds[item.id];
                return (
                  <li key={item.id || i} className="flex items-start gap-2.5 px-3 py-2.5">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={!!checked}
                      disabled={busy || !item.id}
                      onClick={() => handleToggleScopeDone(item)}
                      className="mt-1 flex-shrink-0 flex flex-col items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                      style={{
                        width: "15px",
                        height: "15px",
                        borderRadius: "3.294px",
                        border: checked ? "0 solid #BB42AE" : "1px solid #949494",
                        background: checked ? "#BB42AE" : "#FFFFFF",
                      }}
                    >
                      {checked && (
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                    <span className="text-[14px] leading-relaxed text-[#3F3F46]">
                      {item.text}
                    </span>
                  </li>
                );
              };
              const scopeGroups = groupBySection(scopeItems);
              // No usable headings (older extractions, or a flat RFP): the plain list.
              if (!scopeGroups.length) {
                return (
                  <ul className="divide-y divide-[#EFEFEF]">
                    {scopeItems.map(renderScopeItem)}
                  </ul>
                );
              }
              return scopeGroups.map((g) => {
                const folded = !!collapsedScope[g.head];
                const done = g.items.filter((it) => doneOverrides[it.id] ?? it.done).length;
                return (
                  <section key={g.head} className="border-b border-[#EFEFEF] last:border-b-0">
                    <button
                      type="button"
                      aria-expanded={!folded}
                      onClick={() => setCollapsedScope((p) => ({ ...p, [g.head]: !p[g.head] }))}
                      className="sticky top-0 z-[1] flex w-full items-center justify-between gap-3 bg-[#FAFAFA] px-3 py-2.5 text-left transition-colors hover:bg-[#F4F4F5]"
                    >
                      <span className="min-w-0 truncate text-[14px] font-semibold text-[#3F3F46]">{g.head}</span>
                      <span className="flex flex-none items-center gap-2 text-[12.5px] text-[#707070]">
                        {done > 0 && <span>{done} of {g.items.length} done</span>}
                        {done === 0 && <span>{g.items.length} {g.items.length === 1 ? "item" : "items"}</span>}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={`transition-transform ${folded ? "-rotate-90" : ""}`}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </span>
                    </button>
                    {!folded && (
                      <ul className="divide-y divide-[#EFEFEF]">{g.items.map(renderScopeItem)}</ul>
                    )}
                  </section>
                );
              });
            })()
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <ScreeningQuestionsTable
            displayedData={screening}
            loading={false}
            isAllChecked={false}
            handleSelectAll={() => {}}
            selectedReqs={[]}
            handleCheckboxChange={() => {}}
            searchQuery={search}
            handleViewSource={handleScreeningViewSource}
            showAnswerColumns={data?.screening_status === "done"}
          />
        </div>
      )}

      <SourceViewModal isOpen={isSourceOpen} onClose={() => setIsSourceOpen(false)} data={sourceData} />

      <AddRequirementModal
        isOpen={addReqOpen}
        onClose={() => setAddReqOpen(false)}
        onSubmit={handleAddRequirement}
        submitting={addingReq}
      />
    </div>
  );
};

const RequirementsTab = () => {
  const {
    fetchFiles,
    runRequirementAnalysis,
    requirementAnalyzing,
    requirementAnalysisReady,
    requirementsExtracted,
    requirementStatusKnown,
    runScreeningAnalysis,
    screeningAnalyzing,
    alignmentResult,
    fetchAlignment,
    resumeExtraction,
  } = useOutletContext();
  const match = window.location.pathname.match(/\/project\/(\d+)/);
  const folderId = match ? parseInt(match[1], 10) : null;

  const [activeSubTab, setActiveSubTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedReqs, setSelectedReqs] = useState([]);

  const [controlsPortalNode, setControlsPortalNode] = useState(null);
  const [footerPortalNode, setFooterPortalNode] = useState(null);

  // Popover & Modal State
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(null);
  const [createTaskId, setCreateTaskId] = useState(null);
  const [deleteReqId, setDeleteReqId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // API Data & Analysis State
  const [rawData, setRawData] = useState(null);
  const [isInitialFetching, setIsInitialFetching] = useState(true);
  const [analysisStatus, setAnalysisStatus] = useState("not_started");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalysisPopoverOpen, setIsAnalysisPopoverOpen] = useState(false);

  // Screening Answers State
  const [screeningStatus, setScreeningStatus] = useState("not_started");

  // FIXED: Refs for click outside handling
  const popoverRef = useRef(null);
  const viewAnalysisButtonRef = useRef(null);
  
  // Track previous analysis status to auto-open the popover
  const prevAnalysisStatus = useRef(analysisStatus);

  useEffect(() => {
    // If analysis was just running and now it is done, auto-open the popover
    if (prevAnalysisStatus.current === "running" && analysisStatus === "done") {
      setIsAnalysisPopoverOpen(true);
    }
    prevAnalysisStatus.current = analysisStatus;
  }, [analysisStatus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isAnalysisPopoverOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        viewAnalysisButtonRef.current &&
        !viewAnalysisButtonRef.current.contains(event.target)
      ) {
        setIsAnalysisPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAnalysisPopoverOpen]);

  useEffect(() => {
    setControlsPortalNode(document.getElementById("project-tab-controls-portal"));
    setFooterPortalNode(document.getElementById("project-tab-footer-portal"));
  }, []);

  const fetchInitialStatus = useCallback(
    async (isBackground = false) => {
      if (!folderId) return;

      if (!isBackground) {
        setIsInitialFetching(true);
      }

      try {
        const orgId = Cookies.get("selected_org_id");
        const res = await api.get(
          orgId
            ? `/api/v1/rfp/folders/${folderId}/requirements/alignment/?organization_id=${encodeURIComponent(orgId)}`
            : `/api/v1/rfp/folders/${folderId}/requirements/alignment/`
        );
        const data = res.data;

        if (data?.documents || data?.screening_questions) {
          setRawData(data);
        }

        // General Analysis Status — only "running" keeps polling; done/failed/
        // not_started are terminal so a failed run doesn't loop forever.
        if (data?.alignment_status === "done") {
          setAnalysisStatus("done");
          setAnalysisResult(data);
        } else if (data?.alignment_status === "running") {
          setAnalysisStatus("running");
        } else {
          setAnalysisStatus("not_started");
        }

        // Screening Answers Status
        if (data?.screening_status === "done") {
          setScreeningStatus("done");
        } else if (data?.screening_status === "running") {
          setScreeningStatus("running");
        } else {
          setScreeningStatus("not_started");
        }
      } catch (error) {
        console.error("Error fetching alignment status:", error);
        setAnalysisStatus("not_started");
        setScreeningStatus("not_started");
      } finally {
        if (!isBackground) {
          setIsInitialFetching(false);
        }
      }
    },
    [folderId]
  );

  // NOTE: Alignment status/polling is owned by ProjectPage (single source of
  // truth, drives the header "View Analysis" button + the tab's processing
  // screen via context). The on-mount fetch and the recursive poll that used to
  // live here were removed to avoid duplicate /requirements/alignment/ requests.

  // Derived state for insights processing check across all documents
  const isInsightsProcessing = useMemo(() => {
    if (!rawData || !Array.isArray(rawData.documents)) return false;
    return rawData.documents.some((doc) => doc.insights_status === "processing");
  }, [rawData]);

  // Check if anything requires us to keep polling
  const shouldPoll = isInsightsProcessing || analysisStatus === "running" || screeningStatus === "running";
  
  // Single comprehensive loading state for tables
  const displayLoading = isInitialFetching || shouldPoll;

  const handleRunAnalysis = async () => {
    if (!folderId) return;
    try {
      setAnalysisStatus("running");
      const orgId = Cookies.get("selected_org_id");
      await api.post(
        orgId
          ? `/api/v1/rfp/folders/${folderId}/requirements/alignment/?organization_id=${encodeURIComponent(orgId)}`
          : `/api/v1/rfp/folders/${folderId}/requirements/alignment/`
      );
    } catch (error) {
      console.error("Error running analysis:", error);
      toast.error("Failed to start analysis");
      setAnalysisStatus("not_started");
    }
  };

  const handleGenerateAnswers = async () => {
    if (!folderId) return;
    try {
      setScreeningStatus("running");
      const orgId = Cookies.get("selected_org_id");
      const response = await api.post(
        orgId
          ? `/api/v1/rfp/folders/${folderId}/requirements/screening/?organization_id=${encodeURIComponent(orgId)}`
          : `/api/v1/rfp/folders/${folderId}/requirements/screening/`
      );
      toast.success(response.data.message || "Generating answers started");
    } catch (error) {
      console.error("Error generating answers:", error);
      toast.error("Failed to generate answers");
      setScreeningStatus("not_started");
    }
  };

  const allRequirements = useMemo(() => {
    if (!rawData || !rawData.documents) return [];
    return rawData.documents.reduce((acc, doc) => {
      // Only map requirements if the document processing is completed or if it lacks insights tracking
      if (doc.insights_status === "completed" || !doc.insights_status) {
        const reqs = (doc.requirements || []).map((req) => ({
          ...req,
          parentDocId: doc.doc_id,
          document_name: req.document_name || doc.doc_name,
        }));
        return [...acc, ...reqs];
      }
      return acc;
    }, []);
  }, [rawData]);

  const screeningQuestions = useMemo(() => {
    if (!rawData || !rawData.screening_questions) return [];
    return rawData.screening_questions;
  }, [rawData]);

  const filteredData = useMemo(() => {
    const baseData = activeSubTab === "all" ? allRequirements : screeningQuestions;
    if (!searchQuery.trim()) return baseData;

    const lowerQuery = searchQuery.toLowerCase();
    return baseData.filter(
      (item) =>
        (item.text && item.text.toLowerCase().includes(lowerQuery)) ||
        (item.source_text && item.source_text.toLowerCase().includes(lowerQuery)) ||
        (item.answer && item.answer.toLowerCase().includes(lowerQuery))
    );
  }, [activeSubTab, allRequirements, screeningQuestions, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedReqs([]);
    setIsActionMenuOpen(null);
    setCreateTaskId(null);
    setDeleteReqId(null);
  }, [activeSubTab, searchQuery]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const isAllChecked = selectedReqs.length === paginatedData.length && paginatedData.length > 0;

  const handleSelectAll = () => {
    if (isAllChecked) setSelectedReqs([]);
    else setSelectedReqs(paginatedData.map((item) => item.id));
  };

  const handleCheckboxChange = (id) => {
    setSelectedReqs((prev) =>
      prev.includes(id) ? prev.filter((reqId) => reqId !== id) : [...prev, id]
    );
  };

  const toggleActionMenu = (id, event) => {
    event.stopPropagation();
    setIsActionMenuOpen((prev) => (prev === id ? null : id));
    setCreateTaskId(null);
    setDeleteReqId(null);
  };

  // FIXED: Ensure screening questions map their sources[] array to root properties so the modal can read them.
  const handleViewSource = (item, event) => {
    event.preventDefault();
    event.stopPropagation();

    let processedItem = { ...item };

    // If it's a screening question with sources, promote the first source to root properties
    if (processedItem.sources && processedItem.sources.length > 0) {
      const topSource = processedItem.sources[0];
      processedItem = {
        ...processedItem,
        document_url: topSource.file_url,
        url: topSource.file_url,
        document_name: topSource.file_name,
        name: topSource.file_name,
        source_text: topSource.snippet,
        highlightText: topSource.snippet,
        page_number: topSource.page,
        page: topSource.page,
      };
    }

    setModalData(processedItem);
    setIsModalOpen(true);
  };

  const handleCreateTaskSubmit = async (reqId, taskName) => {
    if (!folderId) throw new Error("Missing folderId");
    try {
      const targetReq =
        allRequirements.find((req) => req.id === reqId) ||
        screeningQuestions.find((req) => req.id === reqId);
      const explanationText = targetReq ? targetReq.text || targetReq.source_text || taskName : taskName;

      const payload = {
        folder_id: folderId,
        heading: explanationText,
        explanation: taskName,
        requirement_id: reqId,
      };

      await api.post(`/api/v1/rfp/insights/tasks/add/`, payload);
      toast.success("Task created successfully");
      await fetchInitialStatus(true);
    } catch (error) {
      toast.error("Failed to create task");
      throw error;
    }
  };

  const handleDeleteRequirementSubmit = async (reqId, docId) => {
    if (!folderId) throw new Error("Missing folderId");
    try {
      const payload = { folder_id: folderId, doc_id: docId, requirement_id: reqId };
      await api.post(`/api/v1/rfp/insights/requirements/delete/`, payload);
      toast.success("Requirement deleted successfully");
      setDeleteReqId(null);
      await fetchInitialStatus(true);
    } catch (error) {
      toast.error("Failed to delete requirement");
    }
  };

  const TabControls = (
    <div className="flex items-center gap-3 ml-auto pb-1 relative">
      <div className="relative w-[280px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
        <input
          type="text"
          placeholder={activeSubTab === "all" ? "Search Requirements..." : "Search Questions..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-1.5 border border-[#00000036] rounded text-[14px] font-outfit focus:outline-none focus:border-[#C95EBE] transition"
          style={{ height: "38px" }}
        />
      </div>

      {/* Conditionally hide Generate Answers button if screeningStatus is "done" */}
      {activeSubTab === "screening" && screeningStatus !== "done" && (
        <button
          onClick={handleGenerateAnswers}
          className={`flex items-center gap-2 bg-gradient-to-br from-[#6366F1] to-[#8B55DD] text-white px-3 py-1 rounded-md font-medium text-[14px] transition-colors ${
            displayLoading
              ? "opacity-75 cursor-not-allowed"
              : "opacity-[0.94] hover:opacity-100 shadow-sm"
          }`}
          style={{ height: "38px" }}
          disabled={displayLoading}
        >
          {screeningStatus === "running" ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <SparklingIcon />
              Generate Answers
            </>
          )}
        </button>
      )}

      {/* Conditionally hide Analysis buttons and popover on screening tab */}
      {activeSubTab !== "screening" && (
        <>
          {analysisStatus === "done" && !isInitialFetching && !shouldPoll ? (
            <button
              ref={viewAnalysisButtonRef}
              className="flex items-center gap-2 bg-white border border-[#D1D5DB] text-[#8B55DD] px-5 py-1.5 rounded-md font-semibold text-[14.5px] transition-colors hover:bg-gray-50 shadow-sm"
              style={{ height: "38px" }}
              onClick={() => setIsAnalysisPopoverOpen(!isAnalysisPopoverOpen)}
            >
              View Analysis
            </button>
          ) : (
            <button
              className={`flex items-center gap-2 bg-gradient-to-br from-[#6366F1] to-[#8B55DD] text-white px-3 py-1 rounded-md font-medium text-[14px] transition-colors ${
                displayLoading
                  ? "opacity-75 cursor-not-allowed"
                  : "opacity-[0.94] hover:opacity-100 shadow-sm"
              }`}
              style={{ height: "38px" }}
              onClick={handleRunAnalysis}
              disabled={displayLoading}
            >
              {displayLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {analysisStatus === "running" ? "Analysing..." : "Hold on..."}
                </>
              ) : (
                <>
                  <SparklingIcon />
                  Run Analysis
                </>
              )}
            </button>
          )}

          {isAnalysisPopoverOpen && analysisResult && analysisStatus === "done" && (() => {
            const score = analysisResult.alignment_score ?? 0;
            const verdictCfg = getVerdictConfig(score, analysisResult.verdict);

            return (
              <div
                ref={popoverRef}
                className="absolute top-[48px] right-0 z-[100] w-[400px] bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-[#E5E5E5] flex flex-col font-outfit text-left"
              >
                <div className="flex items-center justify-between px-4 bg-[#F0E5FF52] py-2 border-b border-[#E5E5E5]">
                  <div className="flex items-center gap-2">
                    <span className="text-[#8B5CF6]">
                      <SparklingIcon2 />
                    </span>
                    <span className="font-semibold text-[16.5px] text-[#8B55DD]">AI Analysis Result</span>
                  </div>
                  <button
                    onClick={() => setIsAnalysisPopoverOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <div className="p-3">
                  <div
                    className="rounded-[8px] flex items-stretch p-3 gap-4"
                    style={{ backgroundColor: verdictCfg.bgColor }}
                  >
                    <div className="flex flex-col flex-shrink-0 justify-center min-w-[100px]">
                      <span className="text-[12px] uppercase text-[#00000080] font-semibold tracking-wide mb-1">
                        Readiness Score
                      </span>
                      <span className="text-[38px] font-bold leading-none" style={{ color: verdictCfg.color }}>
                        {score}%
                      </span>
                    </div>
                    <div className="w-px self-stretch" style={{ backgroundColor: "#0000001A" }} />
                    <div className="flex flex-col justify-center min-w-0">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={verdictCfg.color}
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                          <polyline points="9 12 11 14 15 10"></polyline>
                        </svg>
                        <span className="text-[16px] font-semibold" style={{ color: verdictCfg.color }}>
                          {verdictCfg.label}
                        </span>
                      </div>
                      <p className="text-[13.5px] text-[#000000B3] leading-snug">
                        {analysisResult.recommendation || "Analyzed successfully."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );

  const paginationUI = useMemo(() => {
    if (filteredData.length === 0) return null;
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredData.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    );
  }, [filteredData.length, currentPage, totalPages, rowsPerPage]);

  // --- Render gating ---
  // 1. While analysis is running BUT nothing has been extracted yet -> processing
  //    screen. As soon as requirements exist we fall through to the table below.
  if (requirementAnalyzing && !requirementsExtracted)
    return (
      <RequirementAnalysisProcessingScreen
        documents={alignmentResult?.documents}
        onResume={resumeExtraction}
      />
    );
  // 2. Show the results table when alignment is fully done, OR when a run is in
  //    progress and requirements have already been extracted (map them early).
  //    While alignment is still scoring we pass `analysisRunning` so the table
  //    keeps an "Analysing" indicator up until the alignment status is ready.
  if (requirementAnalysisReady || (requirementAnalyzing && requirementsExtracted))
    return (
      <RequirementsResultView
        data={alignmentResult}
        folderId={folderId}
        onRefresh={fetchAlignment}
        onGenerateScreening={runScreeningAnalysis}
        screeningAnalyzing={screeningAnalyzing}
        analysisRunning={requirementAnalyzing && !requirementAnalysisReady}
        onResume={resumeExtraction}
      />
    );
  // 3. Before the alignment status is known (first load), keep the spinner up so
  //    the "Run Analysis" prompt doesn't flash before results render. This is a
  //    data load, not a run, so don't show the "you can leave" hint.
  if (!requirementStatusKnown) return <RequirementAnalysisProcessingScreen showLeaveHint={false} />;
  // 4. Otherwise -> the initial "Run Analysis" prompt.
  return (
    <RequirementAnalysisInitialScreen
      onRun={runRequirementAnalysis}
      isAnalyzing={requirementAnalyzing}
      documents={alignmentResult?.documents}
      onResume={resumeExtraction}
    />
  );

  /* eslint-disable no-unreachable */
  /*
  return (
    <div className="flex flex-col flex-1 w-full pl-2 pr-1 pb-2 overflow-hidden bg-white">
      {controlsPortalNode && createPortal(TabControls, controlsPortalNode)}

      <div className="flex items-center w-max border border-[#0000001A] rounded-t-lg overflow-hidden flex-shrink-0 mb-[-1px] z-10 relative bg-white">
        <button
          onClick={() => setActiveSubTab("all")}
          className={`px-5 py-2 text-[14px] font-medium transition-colors border-r border-[#0000001A] ${
            activeSubTab === "all" ? "bg-[#F7EBF6] text-[#BB42AE]" : "bg-white text-[#707070] hover:bg-gray-50"
          }`}
        >
          All Requirements
        </button>
        <button
          onClick={() => setActiveSubTab("screening")}
          className={`px-5 py-2 text-[14px] font-medium transition-colors ${
            activeSubTab === "screening" ? "bg-[#F7EBF6] text-[#BB42AE]" : "bg-white text-[#707070] hover:bg-gray-50"
          }`}
        >
          Screening Questions
        </button>
      </div>

      {activeSubTab === "all" ? (
        <RequirementsTable
          displayedData={paginatedData}
          loading={displayLoading}
          showAnalysisColumns={analysisStatus === "done"}
          isAllChecked={isAllChecked}
          handleSelectAll={handleSelectAll}
          selectedReqs={selectedReqs}
          handleCheckboxChange={handleCheckboxChange}
          searchQuery={searchQuery}
          toggleActionMenu={toggleActionMenu}
          isActionMenuOpen={isActionMenuOpen}
          setIsActionMenuOpen={setIsActionMenuOpen}
          createTaskId={createTaskId}
          setCreateTaskId={setCreateTaskId}
          deleteReqId={deleteReqId}
          setDeleteReqId={setDeleteReqId}
          handleViewSource={handleViewSource}
          handleCreateTaskSubmit={handleCreateTaskSubmit}
          handleDeleteRequirementSubmit={handleDeleteRequirementSubmit}
        />
      ) : (
        <ScreeningQuestionsTable
          displayedData={paginatedData}
          loading={displayLoading}
          isAllChecked={isAllChecked}
          handleSelectAll={handleSelectAll}
          selectedReqs={selectedReqs}
          handleCheckboxChange={handleCheckboxChange}
          searchQuery={searchQuery}
          toggleActionMenu={toggleActionMenu}
          isActionMenuOpen={isActionMenuOpen}
          setIsActionMenuOpen={setIsActionMenuOpen}
          createTaskId={createTaskId}
          setCreateTaskId={setCreateTaskId}
          deleteReqId={deleteReqId}
          setDeleteReqId={setDeleteReqId}
          handleCreateTaskSubmit={handleCreateTaskSubmit}
          handleDeleteRequirementSubmit={handleDeleteRequirementSubmit}
          handleViewSource={handleViewSource}
          showAnswerColumns={screeningStatus === "done"}
        />
      )}

      {footerPortalNode && createPortal(paginationUI, footerPortalNode)}

      <SourceViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={modalData}
      />
    </div>
  );
  */
};

export default RequirementsTab;